import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchContacts, 
  selectAllContacts, 
  selectContactsLoading, 
  selectContactsStats,
  selectCurrentCountry,
  selectCurrentMonthYear,
  resetLastFetched,
  deleteContactFromStore,
} from '../../Redux/contactsSlice';
import { deleteContact } from '../../Action/api';
import ContactCharts from './ContactCharts';
import ContactTable from './ContactTable';
import MonthYearPicker from '../../Components/MonthYearPicker';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Person,
  Business,
  TrendingUp,
  Refresh,
  Assessment,
  PersonAdd,
  CalendarMonth,
  ArrowBack,
  ArrowForward,
  Delete,
} from '@mui/icons-material';

const startOfCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

export const LeadManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Redux state
  const contacts = useSelector(selectAllContacts);
  const loading = useSelector(selectContactsLoading);
  const stats = useSelector(selectContactsStats);
  const currentCountryFromRedux = useSelector(selectCurrentCountry);
  const currentMonthYearFromRedux = useSelector(selectCurrentMonthYear);
  
  // Local state
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Month/Year selection state
  const [selectedDate, setSelectedDate] = useState(startOfCurrentMonth);
  const [country, setCountry] = useState('India'); // Default country filter

  // Format date for API (e.g., "Feb-2026")
  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).replace(' ', '-');
  };

  // Fetch contacts from API using Redux
  const loadContactsData = useCallback(async () => {
    const monthYear = formatMonthYear(selectedDate);
    console.log('Fetching contacts for:', country, monthYear);
    
    try {
      const result = await dispatch(fetchContacts({ country, monthYear })).unwrap();
      
      setSnackbar({
        open: true,
        message: `${result.count || 0} contact(s) loaded successfully`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error || 'Failed to fetch contacts',
        severity: 'error'
      });
    }
  }, [selectedDate, country, dispatch]);

  // Fetch contacts on mount and when month/country changes
  useEffect(() => {
    const monthYear = formatMonthYear(selectedDate);
    
    // Only fetch if country or monthYear has changed from what's in Redux
    if (country !== currentCountryFromRedux || monthYear !== currentMonthYearFromRedux) {
      console.log('Redux: Params changed, fetching new data');
      loadContactsData();
    } else if (contacts.length === 0) {
      // Or if we have no data at all
      console.log('Redux: No data in store, fetching');
      loadContactsData();
    } else {
      console.log('Redux: Using cached data from store');
    }
  }, [selectedDate, country, currentCountryFromRedux, currentMonthYearFromRedux, contacts.length, loadContactsData]);

  // Calculate stats from contacts (now using Redux stats)
  // stats is already calculated in Redux, so we can use it directly

  const handleRefresh = async () => {
    setRefreshing(true);
    dispatch(resetLastFetched());
    await loadContactsData();
    setTimeout(() => setRefreshing(false), 1000);
  };

  // Always step from the 1st so e.g. Oct 31 -> "Sep 31" can't overflow back into October
  const handlePreviousMonth = () => {
    setSelectedDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setSelectedDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleCurrentMonth = () => {
    setSelectedDate(startOfCurrentMonth());
  };

  const handleViewContact = (contact) => {
    // Navigate to the contact details page using dateTimeStamp as identifier
    navigate(`/contact/${encodeURIComponent(contact.dateTimeStamp)}`, { state: { contact } });
  };

  const handleEditContact = (contact) => {
    navigate(`/contact/${encodeURIComponent(contact.dateTimeStamp)}`, { state: { contact, edit: true } });
  };

  const handleCreateContact = () => {
    navigate('/create-contact');
  };

  const handleDeleteClick = (e, contact) => {
    e.stopPropagation(); // Prevent row click
    setContactToDelete(contact);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setContactToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete) return;

    setDeleting(true);
    setDeleteDialogOpen(false);

    try {
      const result = await deleteContact(contactToDelete.country, contactToDelete.dateTimeStamp);

      if (result.success) {
        // Remove from Redux store
        dispatch(deleteContactFromStore(contactToDelete.dateTimeStamp));
        
        setSnackbar({
          open: true,
          message: 'Contact deleted successfully!',
          severity: 'success'
        });

        setContactToDelete(null);
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to delete contact',
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete contact',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  // Get contact name based on type
  const getContactName = (contact) => {
    return contact.contactType === 'company' ? contact.companyName : contact.fullName;
  };

  // Get contact email based on type
  const getContactEmail = (contact) => {
    return contact.contactType === 'company' ? contact.companyEmail : contact.email;
  };

  // Daily activity: today's count when viewing the current month, otherwise the month's daily average
  const now = new Date();
  const isCurrentMonth =
    selectedDate.getMonth() === now.getMonth() && selectedDate.getFullYear() === now.getFullYear();
  const countAddedOn = (day) =>
    contacts.filter((c) => new Date(c.createdAt).toDateString() === day.toDateString()).length;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const daysInSelectedMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
  const dailyMetric = isCurrentMonth
    ? {
        label: 'Added Today',
        value: countAddedOn(now),
        // Yesterday is only in the loaded data when it falls in this month
        sub: now.getDate() > 1 ? `Yesterday: ${countAddedOn(yesterday)}` : null,
      }
    : {
        label: 'Avg per Day',
        value: Math.round((contacts.length / daysInSelectedMonth) * 10) / 10,
        sub: null,
      };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 3 }}>
      <Container maxWidth="xl">
        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        )}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnackbar({ ...snackbar, open: false })} 
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {!loading && (
          <>
            {/* Header */}
            <Box sx={{ mb: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.25 }}>
                    Contact Management
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Manage your contacts and relationships in one place
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={handleCreateContact}
                    sx={{
                      bgcolor: '#3b82f6',
                      color: '#fff',
                      fontWeight: 600,
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
                      '&:hover': {
                        bgcolor: '#2563eb',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.35)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    Add New Contact
                  </Button>
                  <Tooltip title="Refresh Data">
                    <IconButton 
                      onClick={handleRefresh} 
                      disabled={refreshing}
                      sx={{
                        bgcolor: '#f1f5f9',
                        '&:hover': { bgcolor: '#e2e8f0' },
                        width: 44,
                        height: 44,
                      }}
                    >
                      <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>

              {/* Month Selector */}
              <Box sx={{ px: 2, py: 1.25, mb: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" rowGap={1}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    <CalendarMonth sx={{ color: '#3b82f6' }} />
                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                      Viewing contacts for:
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton onClick={handlePreviousMonth} size="small" sx={{ bgcolor: '#fff', '&:hover': { bgcolor: '#f1f5f9' } }}>
                      <ArrowBack fontSize="small" />
                    </IconButton>
                    <MonthYearPicker value={selectedDate} onChange={setSelectedDate} />
                    <IconButton 
                      onClick={handleNextMonth} 
                      size="small" 
                      sx={{ bgcolor: '#fff', '&:hover': { bgcolor: '#f1f5f9' } }}
                      disabled={selectedDate.getMonth() === new Date().getMonth() && selectedDate.getFullYear() === new Date().getFullYear()}
                    >
                      <ArrowForward fontSize="small" />
                    </IconButton>
                    <Button
                      size="small"
                      onClick={handleCurrentMonth}
                      sx={{
                        ml: 1,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                      }}
                    >
                      Current Month
                    </Button>
                  </Stack>
                </Stack>
              </Box>

            {/* Key Metrics */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {[
                { label: 'Total Contacts', value: stats.totalContacts, icon: <Assessment sx={{ fontSize: 20 }} />, color: '#3b82f6' },
                { label: 'Individual Persons', value: stats.personContacts, icon: <Person sx={{ fontSize: 20 }} />, color: '#3b82f6' },
                { label: 'Companies', value: stats.companyContacts, icon: <Business sx={{ fontSize: 20 }} />, color: '#8b5cf6' },
                { label: dailyMetric.label, value: dailyMetric.value, sub: dailyMetric.sub, icon: <TrendingUp sx={{ fontSize: 20 }} />, color: '#10b981' },
              ].map((metric) => (
                <Grid item xs={6} md={3} key={metric.label}>
                  <Card sx={{
                    borderRadius: 2,
                    height: '100%',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    border: '1px solid #f1f5f9',
                  }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            borderRadius: 1.5,
                            bgcolor: `${metric.color}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: metric.color,
                          }}
                        >
                          {metric.icon}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.1 }}>
                            {metric.value || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem', lineHeight: 1.3 }}>
                            {metric.label}
                          </Typography>
                          {metric.sub && (
                            <Typography variant="caption" sx={{ color: '#94a3b8', lineHeight: 1.2 }}>
                              {metric.sub}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Charts */}
            <ContactCharts contacts={contacts} selectedDate={selectedDate} onViewContact={handleViewContact} />

            {/* Contact Directory */}
            <ContactTable
              contacts={contacts}
              monthLabel={selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              country={country}
              onCountryChange={setCountry}
              onView={handleViewContact}
              onEdit={handleEditContact}
              onDelete={handleDeleteClick}
              deletingId={deleting ? contactToDelete?.dateTimeStamp : null}
            />
          </>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#1e293b' }}>
            Delete Contact
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ color: '#64748b' }}>
              Are you sure you want to delete this contact? This action cannot be undone.
            </DialogContentText>
            {contactToDelete && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#fef2f2', borderRadius: 2, border: '1px solid #fee2e2' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626', mb: 1 }}>
                  {getContactName(contactToDelete)}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {getContactEmail(contactToDelete)}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={handleDeleteCancel}
              disabled={deleting}
              sx={{ textTransform: 'none', color: '#64748b' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#ef4444',
                '&:hover': { bgcolor: '#dc2626' },
              }}
            >
              {deleting ? 'Deleting...' : 'Delete Contact'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default LeadManagement;
